import React, { useState, useEffect } from 'react';
import { Skeleton } from '../../components/ui/Skeleton';

export default function HomeDashboard() {
  const [isLoading, setIsLoading] = useState(true);

  // Simulate data fetching
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Intelligence Overview</h2>
          <p className="text-sm text-slate-500 mt-1">Precision analysis for your high-value targets.</p>
        </div>
        <div className="flex bg-white rounded-lg border border-slate-200 p-1">
          <button className="px-4 py-1 text-xs font-semibold rounded-md bg-brand-50 text-brand-900">24H</button>
          <button className="px-4 py-1 text-xs font-medium text-slate-500 hover:text-slate-900">7D</button>
          <button className="px-4 py-1 text-xs font-medium text-slate-500 hover:text-slate-900">30D</button>
        </div>
      </div>

      {/* Top Stats Grid */}
      <div className="grid grid-cols-4 gap-6">
        {/* Precision Index Card (Brand colored) */}
        <div className="bg-[#e9ecfb] rounded-xl p-6 border border-[#d1d8f7] relative overflow-hidden">
          {isLoading ? (
             <div className="space-y-4">
               <Skeleton className="h-4 w-24" />
               <Skeleton className="h-12 w-32" />
               <Skeleton className="h-4 w-full" />
               <Skeleton className="h-4 w-2/3" />
             </div>
          ) : (
            <>
              <p className="text-xs font-bold text-brand-900 uppercase tracking-wide">Precision Index</p>
              <div className="mt-4 flex items-end gap-2">
                <span className="text-5xl font-black text-slate-900 tracking-tight">94.2</span>
                <span className="text-sm font-semibold text-brand-700 mb-1">+2.4%</span>
              </div>
              <p className="text-sm text-slate-600 mt-4 leading-relaxed max-w-[80%]">Data fidelity based on current lead engagement signals.</p>
              <div className="mt-8 flex gap-8 border-t border-[#d1d8f7] pt-4">
                <div>
                  <p className="text-[10px] text-slate-500 font-semibold uppercase">Verified</p>
                  <p className="text-lg font-bold text-slate-900">12.8k</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 font-semibold uppercase">Reach Rate</p>
                  <p className="text-lg font-bold text-slate-900">68%</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Standard Stat Cards */}
        {[
          { title: 'New Leads Identified', value: '2,482', trend: '+ 14% vs last period', positive: true },
          { title: 'Active Sequences', value: '412', text: 'Running across 12 sectors' },
          { title: 'Conversion Velocity', value: '4.2d', trend: '- 0.5d optimization', positive: false } // false for red text
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm flex flex-col justify-center">
            {isLoading ? (
              <div className="space-y-3">
                 <Skeleton className="h-4 w-1/2" />
                 <Skeleton className="h-10 w-24" />
                 <Skeleton className="h-3 w-2/3" />
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                <p className="text-4xl font-bold text-slate-900 mt-3">{stat.value}</p>
                {stat.trend && <p className={`text-xs mt-3 font-medium ${stat.positive ? 'text-green-600' : 'text-red-500'}`}>{stat.trend}</p>}
                {stat.text && <p className="text-xs mt-3 text-slate-500 font-medium">{stat.text}</p>}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Table Area */}
        <div className="col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900">Top Business Targets</h3>
            <button className="text-sm font-semibold text-brand-700 hover:text-brand-900">View Full List →</button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                  <th className="p-4">Lead Entity</th>
                  <th className="p-4">Signal Score</th>
                  <th className="p-4">Industry Alignment</th>
                  <th className="p-4">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  [...Array(3)].map((_, i) => (
                    <tr key={i}>
                      <td className="p-4"><div className="flex items-center gap-3"><Skeleton className="h-10 w-10 rounded-lg" /><Skeleton className="h-4 w-32" /></div></td>
                      <td className="p-4"><Skeleton className="h-2 w-16" /></td>
                      <td className="p-4"><Skeleton className="h-6 w-20 rounded-full" /></td>
                      <td className="p-4"><Skeleton className="h-4 w-16" /></td>
                      <td className="p-4"></td>
                    </tr>
                  ))
                ) : (
                  [
                    { initials: 'NV', name: 'Nexus Ventures', loc: 'San Francisco, CA', score: 92, ind: 'FINTECH', status: 'Hot', color: 'bg-green-500' },
                    { initials: 'AL', name: 'Aether Labs', loc: 'London, UK', score: 78, ind: 'DEEP TECH', status: 'Nurturing', color: 'bg-blue-500' },
                    { initials: 'SI', name: 'Solaris Industries', loc: 'Austin, TX', score: 64, ind: 'ENERGY', status: 'Contacted', color: 'bg-yellow-500' }
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#eff4ff] text-brand-900 flex items-center justify-center font-bold text-sm">{row.initials}</div>
                        <div>
                          <p className="text-sm font-bold text-slate-900">{row.name}</p>
                          <p className="text-[11px] text-slate-500">{row.loc}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-brand-900" style={{ width: `${row.score}%` }}></div>
                          </div>
                          <span className="text-xs font-bold text-slate-700">{row.score}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 text-[10px] font-bold text-slate-600 bg-slate-100 rounded-full">{row.ind}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${row.color}`}></div>
                          <span className="text-xs font-medium text-slate-700">{row.status}</span>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <button className="text-slate-400 hover:text-slate-600">⋮</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Right Sidebar Area */}
        <div className="space-y-6">
            <div className="bg-[#f0ebff] rounded-xl p-6 border border-[#e5ddff] shadow-sm">
                {/* Top Lists Card implementation here based on screenshot */}
                <h3 className="text-sm font-bold text-slate-900 mb-4">Top Lists</h3>
                {isLoading ? <Skeleton className="h-20 w-full rounded-lg" /> : (
                  <div className="bg-white rounded-lg p-3 shadow-sm mb-3">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-slate-900">SaaS Founders Q4</span>
                        <span className="text-xs text-slate-500 text-right leading-tight">142<br/>leads</span>
                    </div>
                    <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden"><div className="w-3/4 h-full bg-brand-900"></div></div>
                  </div>
                )}
            </div>
            
             <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <h3 className="text-sm font-bold text-slate-900 mb-4">Activity Stream</h3>
                {/* Activity Stream Implementation */}
            </div>
        </div>
      </div>
    </div>
  );
}